import { useState } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { format as formatDate, subDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { DateRangePicker } from './DateRangePicker';
import { ColumnSelector } from './ColumnSelector';
import {
  exportQuestions,
  downloadBlob,
  getExportFilename,
  DEFAULT_COLUMNS,
  type ExportFormat,
  type ExportColumn,
} from '@/lib/exportApi';
import { QuestionStatus, type QuestionFilters } from '@/types';

const STATUS_OPTIONS: { value: QuestionStatus; label: string }[] = [
  { value: QuestionStatus.PENDING, label: 'Pending' },
  { value: QuestionStatus.TRIAGED, label: 'Triaged' },
  { value: QuestionStatus.ASSIGNED, label: 'Assigned' },
  { value: QuestionStatus.ANSWERED, label: 'Answered' },
  { value: QuestionStatus.SENT, label: 'Sent' },
  { value: QuestionStatus.REJECTED, label: 'Rejected' },
];

interface ExportDialogProps {
  filters?: QuestionFilters;
}

export function ExportDialog({ filters }: ExportDialogProps) {
  const [open, setOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('excel');
  const [dateFrom, setDateFrom] = useState<string | undefined>(() =>
    formatDate(subDays(new Date(), 30), 'yyyy-MM-dd')
  );
  const [dateTo, setDateTo] = useState<string | undefined>(() =>
    formatDate(new Date(), 'yyyy-MM-dd')
  );
  const [columns, setColumns] = useState<ExportColumn[]>([...DEFAULT_COLUMNS]);
  const [selectedStatuses, setSelectedStatuses] = useState<QuestionStatus[]>(() =>
    filters?.status?.length ? [...filters.status] : [QuestionStatus.ANSWERED]
  );
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDateChange = (from: string | undefined, to: string | undefined) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const handleStatusToggle = (status: QuestionStatus, checked: boolean) => {
    if (checked) {
      setSelectedStatuses((prev) => [...prev, status]);
    } else {
      setSelectedStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleExport = async () => {
    if (columns.length === 0) {
      setError('Please select at least one column');
      return;
    }
    setIsExporting(true);
    setError(null);

    try {
      const blob = await exportQuestions({
        format: exportFormat,
        dateFrom,
        dateTo,
        columns,
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        source: filters?.source,
      });

      downloadBlob(blob, getExportFilename(exportFormat));
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="sm:mr-1 h-4 w-4" />
          <span className="hidden sm:inline">Export</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export Questions</DialogTitle>
          <DialogDescription>
            Download questions data as CSV or Excel file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Format Selection */}
          <div className="space-y-3">
            <span className="text-sm font-medium">Export Format</span>
            <div className="flex gap-2">
              <Button
                variant={exportFormat === 'excel' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setExportFormat('excel')}
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Excel
              </Button>
              <Button
                variant={exportFormat === 'csv' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setExportFormat('csv')}
              >
                <FileText className="mr-2 h-4 w-4" />
                CSV
              </Button>
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-3">
            <span className="text-sm font-medium">Status Filter</span>
            <div className="grid grid-cols-2 gap-2 rounded-lg border p-3">
              {STATUS_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${option.value}`}
                    checked={selectedStatuses.includes(option.value)}
                    onCheckedChange={(checked) =>
                      handleStatusToggle(option.value, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`status-${option.value}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedStatuses.length === 0
                ? 'No status selected — all statuses will be exported'
                : `${selectedStatuses.length} status${selectedStatuses.length > 1 ? 'es' : ''} selected`}
            </p>
          </div>

          {/* Date Range */}
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={handleDateChange}
          />

          {/* Column Selection */}
          <ColumnSelector selectedColumns={columns} onChange={setColumns} />

          {/* Error Message */}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export {exportFormat === 'excel' ? 'Excel' : 'CSV'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
